import React, { useEffect, useMemo } from 'react';
import { Table, Card, Row, Col, Typography, Spin, Tabs, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStockFinancials } from '../../api/tickersApi';
import _ from 'lodash';
import { DollarOutlined, BarChartOutlined, BankOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { TabPane } = Tabs;


const FinancialStatements = () => {
  const formatNumber = (value) => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact',
      compactDisplay: 'short'
    }).format(value);
  };

  // Balance Sheet columns
  const balanceSheetColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span style={{ paddingLeft: record.level * 20 }}>{text}</span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (value) => formatNumber(value),
    },
  ];

  // Balance Sheet data
  const balanceSheetData = [
    { key: '1', name: 'Assets', value: 430266000000, level: 0 },
    { key: '2', name: 'Current Assets', value: 157541000000, level: 1 },
    { key: '3', name: 'Cash and Short Term Investments', value: 93230000000, level: 2 },
    { key: '4', name: 'Net Receivables', value: 49104000000, level: 2 },
    { key: '5', name: 'Other Current Assets', value: 15207000000, level: 2 },
    { key: '6', name: 'Non-Current Assets', value: 272725000000, level: 1 },
    { key: '7', name: 'Property, Plant & Equipment (Net)', value: 174831000000, level: 2 },
    { key: '8', name: 'Goodwill', value: 31935000000, level: 2 },
    { key: '9', name: 'Long Term Investments', value: 36177000000, level: 2 },
    { key: '10', name: 'Liabilities', value: 116147000000, level: 0 },
    { key: '11', name: 'Current Liabilities', value: 80803000000, level: 1 },
    { key: '12', name: 'Accounts Payable', value: 7049000000, level: 2 },
    { key: '13', name: 'Short Term Debt', value: 2971000000, level: 2 },
    { key: '14', name: 'Other Current Liabilities', value: 63248000000, level: 2 },
    { key: '15', name: 'Non-Current Liabilities', value: 35344000000, level: 1 },
    { key: '16', name: 'Long Term Debt', value: 10880000000, level: 2 },
    { key: '17', name: 'Stockholders\' Equity', value: 314119000000, level: 0 },
    { key: '18', name: 'Common Stock', value: 82030000000, level: 1 },
    { key: '19', name: 'Retained Earnings', value: 235317000000, level: 1 },
  ];

  // Income Statement data
  const incomeStatementData = [
    { key: '1', name: 'Revenue', value: 88268000000, level: 0 },
    { key: '2', name: 'Cost of Revenue', value: 36474000000, level: 1 },
    { key: '3', name: 'Gross Profit', value: 51794000000, level: 0 },
    { key: '4', name: 'Operating Expenses', value: 23273000000, level: 0 },
    { key: '5', name: 'Research & Development', value: 12447000000, level: 1 },
    { key: '6', name: 'Selling & Marketing', value: 7227000000, level: 1 },
    { key: '7', name: 'General & Administrative', value: 3599000000, level: 1 },
    { key: '8', name: 'Operating Income', value: 28521000000, level: 0 },
    { key: '9', name: 'Interest Income', value: 1243000000, level: 1 },
    { key: '10', name: 'Interest Expense', value: 54000000, level: 1 },
    { key: '11', name: 'Income Before Tax', value: 31706000000, level: 0 },
    { key: '12', name: 'Income Tax Expense', value: 5405000000, level: 1 },
    { key: '13', name: 'Net Income', value: 26301000000, level: 0 },
  ];

  // Cash Flow Statement data
  const cashFlowData = [
    { key: '1', name: 'Operating Activities', value: 30698000000, level: 0 },
    { key: '2', name: 'Change in Working Capital', value: -3789000000, level: 1 },
    { key: '3', name: 'Depreciation & Amortization', value: 3985000000, level: 1 },
    { key: '4', name: 'Stock Based Compensation', value: 5846000000, level: 1 },
    { key: '5', name: 'Investing Activities', value: -33532000000, level: 0 },
    { key: '6', name: 'Capital Expenditures', value: -13061000000, level: 1 },
    { key: '7', name: 'Investments', value: -18011000000, level: 1 },
    { key: '8', name: 'Financing Activities', value: -20094000000, level: 0 },
    { key: '9', name: 'Dividends Paid', value: -2455000000, level: 1 },
    { key: '10', name: 'Stock Repurchase', value: -15291000000, level: 1 },
    { key: '11', name: 'Net Change in Cash', value: -7266000000, level: 0 },
    { key: '12', name: 'Beginning Cash Balance', value: 27225000000, level: 1 },
    { key: '13', name: 'Ending Cash Balance', value: 19959000000, level: 1 },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Balance Sheet */}
      <Card
        title={
          <Space>
            <BankOutlined />
            <Title level={4} style={{ margin: 0 }}>Balance Sheet</Title>
          </Space>
        }
      >
        <Table
          columns={balanceSheetColumns}
          dataSource={balanceSheetData}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Income Statement */}
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Title level={4} style={{ margin: 0 }}>Income Statement</Title>
          </Space>
        }
      >
        <Table
          columns={balanceSheetColumns}
          dataSource={incomeStatementData}
          pagination={false}
          size="middle"
        />
      </Card>

      {/* Cash Flow Statement */}
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <Title level={4} style={{ margin: 0 }}>Cash Flow Statement</Title>
          </Space>
        }
      >
        <Table
          columns={balanceSheetColumns}
          dataSource={cashFlowData}
          pagination={false}
          size="middle"
        />
      </Card>
    </Space>
  );
};
const StockFinancials = ({ symbol }) => {
    const dispatch = useDispatch();

    // Get financials data DIRECTLY from the tickers slice
    const financialsData = useSelector(state =>
        state.tickers.tickers[symbol]?.financials || []
    );

    // Create a separate loading selector for financials
    const loading = useSelector(state => state.tickers.status === 'loading');

    useEffect(() => {
        if (symbol) {
            dispatch(fetchStockFinancials({ symbol }));
        }
    }, [symbol, dispatch]);

    // Debug logging
    useEffect(() => {
    }, [financialsData]);

    const balanceSheetColumns = useMemo(() => [
        // ... rest of your columns remain the same
    ], []);

    const sortedData = useMemo(() => {
        return _.orderBy(financialsData || [], ['timestamp'], ['desc']);
    }, [financialsData]);

    return (
        <Card>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Title level={3}>Financial Data for {symbol}</Title>
                </Col>
                <Col span={24}>
                    <Spin spinning={loading}>
                        <Tabs defaultActiveKey="balance">
                            <TabPane tab="Balance Sheet" key="balance">
                                <Table
                                    columns={balanceSheetColumns}
                                    dataSource={sortedData}
                                    rowKey="timestamp"
                                    scroll={{ x: true }}
                                    pagination={{
                                        pageSize: 8,
                                        showSizeChanger: true,
                                        showTotal: (total) => `Total ${total} items`
                                    }}
                                    bordered
                                />
                            </TabPane>
                            {/* ... rest of your tabs remain the same */}
                        </Tabs>
                    </Spin>
                </Col>
            </Row>
        </Card>
    );
};

export default StockFinancials;